import { useState } from "react";

import { css } from "@compiled/react";

import { Row, TypographyStyles } from "@/libraries/wpl/foundations/src";

import { lastUpdated, cdnLibraryData } from "./cdn-library-data";

const CDNLibrary = () => {
    const [bgColor, setBgColor] = useState("#f0f0f0");

    // Redirect to the homepage if the page is accessed from atlassian.com
    if (window?.location.hostname === "atlassian.com") {
        window.location.href = "/";
    }

    return (
        <div
            css={css({
                padding: "3rem 0",
                minHeight: "100vh",
                backgroundColor: bgColor
            })}
        >
            <TypographyStyles>
                <div
                    css={css({
                        position: "fixed",
                        insetBlockStart: 0,
                        display: "flex",
                        justifyContent: "space-between",
                        gap: "3rem",
                        padding: "2rem",
                        width: "100%",
                        boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
                        backgroundColor: bgColor
                    })}
                >
                    <input
                        type="color"
                        value={bgColor}
                        onChange={(e) => setBgColor(e.target.value)}
                        title="Change background color of the page"
                    />
                    <span>
                        <div>
                            <b>Last updated:</b>
                        </div>
                        <div>{new Date(lastUpdated).toLocaleString()}</div>
                        <div>{calculateHowLongAgo(new Date(lastUpdated))}</div>
                    </span>
                </div>
                <Row>
                    <div
                        css={css({
                            paddingBlockStart: "4.5rem",
                            // Tech debt: Adding this disable to unblock commits for GWP-2646
                            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                            h1: { margin: "3rem auto 0", textAlign: "center" }
                        })}
                    >
                        <h1>WAC-WEB CDN Library</h1>
                        <p
                            css={css({
                                margin: "0 auto",
                                maxWidth: 600,
                                textAlign: "center"
                            })}
                        >
                            These are the asset files uploaded to the WAC CDN.
                            Search, filter, and copy the URL of the asset you
                            are looking for quick and easy.
                        </p>
                        <div
                            css={css({
                                display: "flex",
                                flexDirection: "column",
                                gap: "2rem",
                                margin: "3rem 0"
                            })}
                        >
                            {cdnLibraryData.map((section) => (
                                <DirectorySection
                                    key={section.directory}
                                    directory={section.directory}
                                    files={section.files}
                                />
                            ))}
                        </div>
                    </div>
                </Row>
            </TypographyStyles>
        </div>
    );
};

const DirectorySection = ({
    directory,
    files
}: {
    directory: string;
    files: string[];
}) => {
    const [showFiles, setShowFiles] = useState(false);
    const [filter, setFilter] = useState("");

    const filteredFiles = files.filter((file) => file.includes(filter));

    if (files.length === 0) return null;

    return (
        <section>
            <span
                css={css({
                    display: "grid",
                    alignItems: "center",
                    justifyItems: "left",
                    gridTemplateColumns: "2fr 1fr 1fr",
                    gap: "2rem",
                    // Tech debt: Adding this disable to unblock commits for GWP-2646
                    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                    h2: {
                        margin: "1rem",
                        cursor: "pointer"
                    }
                })}
            >
                <h2>{directory}</h2>
                <label
                    htmlFor={directory + "show"}
                    css={css({ cursor: "pointer" })}
                >
                    <input
                        id={directory + "show"}
                        type="checkbox"
                        checked={showFiles}
                        onChange={() => setShowFiles((prev) => !prev)}
                    />
                    Show {files.length === 1 ? "file" : "files"} (
                    {filteredFiles.length})
                </label>
                <label htmlFor={directory + "filter"}>
                    Filter:{" "}
                    <input
                        id={directory + "filter"}
                        type="text"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                    />
                </label>
            </span>
            <div
                css={css({
                    display: showFiles ? "flex" : "none",
                    flexDirection: "column",
                    gap: "2rem"
                })}
            >
                {filteredFiles.map((file) => (
                    <FilePreview key={file} file={file} />
                ))}
            </div>
        </section>
    );
};

const FilePreview = ({ file }: { file: string }) => {
    const copyToClipboard = () => {
        navigator.clipboard.writeText(file);
    };

    return (
        <div
            css={css({
                display: "flex",
                flexDirection: "column",
                placeItems: "start",
                gap: "1rem",
                margin: "0 2rem",
                paddingInlineStart: "1rem",
                borderInlineStart: "4px solid #789",
                // Tech debt: Adding this disable to unblock commits for GWP-2646
                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                a: {
                    color: "blue"
                }
            })}
        >
            <div>
                <p css={css({ display: "flex", gap: "2rem" })}>
                    <button onClick={copyToClipboard} title="Save to clipboard">
                        COPY URL
                    </button>
                    <a href={file} target="_blank" rel="noreferrer">
                        OPEN IN NEW TAB
                    </a>
                </p>
                <span>{file}</span>
            </div>
            <div
                css={css({
                    "maxWidth": 400,
                    // Tech debt: Adding this disable to unblock commits for GWP-2646
                    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                    "video, img": {
                        width: "100%",
                        maxWidth: "100%",
                        maxHeight: "100%"
                    }
                })}
            >
                {isVideo(file) && (
                    // Tech debt: Adding this disable to unblock commits for GWP-2646
                    // eslint-disable-next-line jsx-a11y/media-has-caption
                    <video src={file} controls />
                )}
                {/* eslint-disable-next-line no-restricted-syntax */}
                {isImage(file) && <img src={file} alt={file} loading="lazy" />}
            </div>
        </div>
    );
};

const isVideo = (file: string) => {
    return file.endsWith(".mp4") || file.endsWith(".webm");
};

const isImage = (file: string) => {
    return (
        file.endsWith(".svg") ||
        file.endsWith(".webp") ||
        file.endsWith(".png") ||
        file.endsWith(".jpg") ||
        file.endsWith(".jpeg") ||
        file.endsWith(".gif")
    );
};

const calculateHowLongAgo = (datePast: Date) => {
    // Calculate the difference between now and the datePast
    const difference = new Date().getTime() - datePast.getTime();

    // Convert difference into days, hours, and minutes
    const daysDifference = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hoursDifference = Math.floor(
        (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutesDifference = Math.floor(
        (difference % (1000 * 60 * 60)) / (1000 * 60)
    );

    // Construct the output string
    let result = "";
    if (daysDifference > 0) {
        result += daysDifference + " day" + (daysDifference > 1 ? "s" : "");
    }
    if (daysDifference > 0 && hoursDifference > 0) {
        result += ", ";
    }
    if (hoursDifference > 0) {
        result += hoursDifference + " hour" + (hoursDifference > 1 ? "s" : "");
    }
    if (hoursDifference > 0 && minutesDifference > 0) {
        result += ", and ";
    }
    if (minutesDifference > 0) {
        result +=
            minutesDifference + " minute" + (minutesDifference > 1 ? "s" : "");
    }

    return result + " ago";
};

export default CDNLibrary;
